import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Download, Calendar, Users, 
  Building2, RefreshCw, ChevronDown, Mail, CheckCircle,
  FileSignature, Euro, TrendingUp, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReportsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedCommissionMonth, setSelectedCommissionMonth] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load available reports
      const reportsRes = await fetch(`${API_URL}/api/admin/reports/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setAvailableMonths(data.available_months || []);
        if (data.available_months?.length > 0) {
          setSelectedMonth(data.available_months[0]);
        }
      }
      
      // Load partners for partner report
      const partnersRes = await fetch(`${API_URL}/api/admin/partners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (partnersRes.ok) {
        const data = await partnersRes.json();
        setPartners(data || []);
      }
      
      // Set default commission month
      if (availableMonths.length > 0 && !selectedCommissionMonth) {
        setSelectedCommissionMonth(availableMonths[0]);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadMonthlyReport = async () => {
    if (!selectedMonth) return;
    
    setDownloading('monthly');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/monthly/${selectedMonth.year}/${selectedMonth.month}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `monatsbericht_${selectedMonth.year}_${String(selectedMonth.month).padStart(2, '0')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert('Fehler beim Herunterladen des Reports');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Fehler beim Herunterladen');
    } finally {
      setDownloading(null);
    }
  };

  const downloadPartnerReport = async () => {
    if (!selectedPartner) return;
    
    setDownloading('partner');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/partner/${selectedPartner}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `partner_report_${selectedPartner.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert('Fehler beim Herunterladen des Reports');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Fehler beim Herunterladen');
    } finally {
      setDownloading(null);
    }
  };

  const sendMonthlyReportEmail = async () => {
    if (!selectedMonth) return;
    
    setSendingEmail(true);
    setEmailSent(false);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/send-monthly-email?year=${selectedMonth.year}&month=${selectedMonth.month}`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Email send error:', error);
      alert('Fehler beim Senden der E-Mail');
    } finally {
      setSendingEmail(false);
    }
  };

  // Download agreements report (PDF)
  const downloadAgreementsReport = async () => {
    setDownloading('agreements');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/agreements`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vereinbarungen_report_${new Date().toISOString().slice(0,10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Vereinbarungs-Report heruntergeladen');
      } else {
        toast.error('Fehler beim Herunterladen des Reports');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Herunterladen');
    } finally {
      setDownloading(null);
    }
  };

  // Download agreements CSV
  const downloadAgreementsCsv = async () => {
    setDownloading('agreements-csv');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/agreements/csv`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'vereinbarungen.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV exportiert');
      } else {
        toast.error('Fehler beim Exportieren');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Fehler beim Exportieren');
    } finally {
      setDownloading(null);
    }
  };

  // Download commission report (PDF)
  const downloadCommissionReport = async () => {
    if (!selectedCommissionMonth) return;
    
    setDownloading('commission');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/reports/commission/${selectedCommissionMonth.year}/${selectedCommissionMonth.month}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `provisions_report_${selectedCommissionMonth.year}_${String(selectedCommissionMonth.month).padStart(2, '0')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Provisions-Report heruntergeladen');
      } else {
        toast.error('Fehler beim Herunterladen des Reports');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Herunterladen');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5" />
              PDF Reports
            </h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        
        {/* Monthly Report */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Monatsbericht</h2>
                <p className="text-sm text-gray-600">Übersicht aller Fälle und Partner für einen Monat</p>
              </div>
            </div>
          </div>
          
          <div className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monat auswählen
                </label>
                <div className="relative">
                  <select
                    value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      setSelectedMonth({ year: parseInt(year), month: parseInt(month), label: '' });
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg appearance-none bg-white pr-10"
                  >
                    {availableMonths.map((m, i) => (
                      <option key={i} value={`${m.year}-${m.month}`}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <Button
                onClick={downloadMonthlyReport}
                disabled={downloading === 'monthly' || !selectedMonth}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 w-full sm:w-auto"
              >
                {downloading === 'monthly' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF herunterladen
              </Button>
              
              <Button
                onClick={sendMonthlyReportEmail}
                disabled={sendingEmail || !selectedMonth}
                variant="outline"
                className="gap-2 w-full sm:w-auto border-green-500 text-green-600 hover:bg-green-50"
              >
                {sendingEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : emailSent ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {emailSent ? 'Gesendet!' : 'Per E-Mail senden'}
              </Button>
            </div>
            
            {emailSent && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Report wurde an info@dein-gutachten-direkt.de gesendet!
              </div>
            )}
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Report enthält:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Zusammenfassung: Fälle, Partner, Umsatz</li>
                <li>• Status-Verteilung der Fälle</li>
                <li>• Top 5 Partner des Monats</li>
                <li>• Abschlussquote und Trends</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Partner Report */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Partner-Report</h2>
                <p className="text-sm text-gray-600">Individueller Performance-Report für einen Partner</p>
              </div>
            </div>
          </div>
          
          <div className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Partner auswählen
                </label>
                <div className="relative">
                  <select
                    value={selectedPartner}
                    onChange={(e) => setSelectedPartner(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg appearance-none bg-white pr-10"
                  >
                    <option value="">Partner wählen...</option>
                    {partners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.company_name || p.email}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <Button
                onClick={downloadPartnerReport}
                disabled={downloading === 'partner' || !selectedPartner}
                className="bg-green-600 hover:bg-green-700 text-white gap-2 w-full sm:w-auto"
              >
                {downloading === 'partner' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF herunterladen
              </Button>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Report enthält:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Partner-Informationen und Kontaktdaten</li>
                <li>• Provisions-Stufe und Rate</li>
                <li>• Performance: Fälle, Abschlüsse, Quote</li>
                <li>• Umsatz und verdiente Provisionen</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Agreements Report - NEW */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Vereinbarungs-Report</h2>
                <p className="text-sm text-gray-600">Übersicht aller Partnervereinbarungen und deren Status</p>
              </div>
            </div>
          </div>
          
          <div className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <Button
                onClick={downloadAgreementsReport}
                disabled={downloading === 'agreements'}
                className="bg-orange-600 hover:bg-orange-700 text-white gap-2 w-full sm:w-auto"
                data-testid="download-agreements-report-btn"
              >
                {downloading === 'agreements' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF herunterladen
              </Button>
              
              <Button
                onClick={downloadAgreementsCsv}
                disabled={downloading === 'agreements-csv'}
                variant="outline"
                className="gap-2 w-full sm:w-auto border-orange-500 text-orange-600 hover:bg-orange-50"
                data-testid="download-agreements-csv-btn"
              >
                {downloading === 'agreements-csv' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4" />
                )}
                CSV exportieren
              </Button>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Report enthält:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Alle Partner mit Vereinbarungsstatus</li>
                <li>• Unterschriftsdatum und -details</li>
                <li>• Statistik: Unterschrieben vs. Ausstehend</li>
                <li>• Exportierbar als PDF oder CSV</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Commission Report - NEW */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Euro className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Provisions-Report</h2>
                <p className="text-sm text-gray-600">Detaillierte Provisionsübersicht für Auszahlungen</p>
              </div>
            </div>
          </div>
          
          <div className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zeitraum
                </label>
                <div className="relative">
                  <select
                    value={selectedCommissionMonth ? `${selectedCommissionMonth.year}-${selectedCommissionMonth.month}` : ''}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      setSelectedCommissionMonth({ year: parseInt(year), month: parseInt(month) });
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg appearance-none bg-white pr-10"
                  >
                    {availableMonths.map((m, i) => (
                      <option key={i} value={`${m.year}-${m.month}`}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <Button
                onClick={downloadCommissionReport}
                disabled={downloading === 'commission' || !selectedCommissionMonth}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2 w-full sm:w-auto"
                data-testid="download-commission-report-btn"
              >
                {downloading === 'commission' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF herunterladen
              </Button>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Report enthält:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Auszahlungsübersicht nach Partner</li>
                <li>• Provisionsberechnung pro Fall</li>
                <li>• Netto-Grundhonorar und Provisionsrate</li>
                <li>• Summen für Buchhaltung</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900">PDF-Report Tipps</h3>
              <p className="text-sm text-blue-700 mt-1">
                Die Reports werden als PDF generiert und können direkt ausgedruckt oder per E-Mail versendet werden.
                Für regelmäßige Reports können Sie auch die Analytics-Seite nutzen, die interaktive Grafiken bietet.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminReportsPage;
